import { Clock, PhoneOff, MessageSquareX, ReceiptText, Headphones, Share2, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface PainPoint {
  id: string;
  icon: typeof Clock;
  title: string;
  cost: string;
  aiSolution: string;
}
const PAIN_POINTS: PainPoint[] = [{
  id: "espera",
  icon: Clock,
  title: "Clientes que esperan horas una respuesta",
  cost: "clientes que se van con la competencia.",
  aiSolution: "respuesta inmediata, a cualquier hora."
}, {
  id: "fuera-de-horario",
  icon: PhoneOff,
  title: "Mensajes y llamadas perdidos fuera de horario",
  cost: "ventas que nunca llegan a conocer.",
  aiSolution: "un asistente atiende de noche y fines de semana."
}, {
  id: "repetitivas",
  icon: MessageSquareX,
  title: "Agentes ocupados en preguntas repetidas",
  cost: "salarios pagados por tareas que una máquina hace.",
  aiSolution: "la IA responde lo repetido y su gente atiende lo importante."
}, {
  id: "cobranza",
  icon: ReceiptText,
  title: "Cobranza atrasada y recordatorios manuales",
  cost: "dinero que entra tarde y horas de gestión.",
  aiSolution: "avisos y recordatorios de pago automáticos."
}, {
  id: "llamadas-ciegas",
  icon: Headphones,
  title: "Nadie sabe qué se dice en las llamadas y chats",
  cost: "errores que se repiten y quejas que no se ven.",
  aiSolution: "transcripción y análisis de cada conversación."
}, {
  id: "canales-dispersos",
  icon: Share2,
  title: "Canales separados: WhatsApp, teléfono, correo",
  cost: "clientes repitiendo su historia y datos perdidos.",
  aiSolution: "todos los canales en un solo lugar."
}];
export default function PerdidasEmpresariales() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="perdidas" data-section-id="perdidas" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Section Header */}
          <div className="max-w-3xl mb-12 md:mb-16">
            <Badge variant="secondary" className="mb-4 uppercase tracking-wider font-semibold text-xs">
              LO QUE SU EMPRESA PIERDE
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4 tracking-tight">
              ¿Dónde pierde dinero su empresa?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
              Estos son los problemas que vemos todos los días en las empresas. ¿Reconoce alguno?
            </p>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 md:mb-16">
            {PAIN_POINTS.map(item => {
            const Icon = item.icon;
            return <div key={item.id} className="bg-card text-card-foreground rounded-md border border-border p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:border-primary flex flex-col justify-between">
                  <div>
                    <div className="size-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground mb-5 shrink-0">
                      <Icon className="size-6 text-white" strokeWidth={1.75} />
                    </div>
                    <h3 className="text-lg font-semibold font-default text-foreground mb-4 leading-snug">
                      {item.title}
                    </h3>
                  </div>

                  <div className="space-y-2 pt-2 border-t border-border/60">
                    <p className="text-sm font-default leading-relaxed">
                      <span className="font-semibold text-destructive">Le cuesta: </span>
                      <span className="text-muted-foreground">{item.cost}</span>
                    </p>
                    <p className="text-sm font-default leading-relaxed">
                      <span className="font-semibold text-secondary">Con IA: </span>
                      <span className="text-foreground">{item.aiSolution}</span>
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {/* Highlighted Band */}
          <div className="bg-accent rounded-md border-l-4 border-primary p-6 md:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-3xl">
              <h3 className="text-xl md:text-2xl font-bold font-default text-foreground mb-2">
                Antes de instalar nada, buscamos dónde pierde usted.
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-default leading-relaxed">
                AVTECSA analiza sus canales de atención, mide dónde se va el tiempo y el dinero, y empieza por donde su empresa más pierde.
              </p>
            </div>
            <Button asChild size="lg" className="bg-chart-4 hover:bg-chart-4/90 text-black font-bold rounded-md px-6 py-6 text-base shadow-md transition-all shrink-0 w-full sm:w-auto">
              <Link to="#contacto">
                Descubra dónde pierde dinero
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}